/**
 * Equipment Catalog domain types — Prisma-agnostic (STORY-1106, ADR-123 D-1).
 *
 * These are the pure domain shapes the service + repositories exchange.
 * Prisma-generated types live in `apps/kgc-api` and are mapped at adapter
 * boundaries (repository implementations).
 *
 * Frontend-safe (NO NestJS imports) — Vite/Rollup bundles cleanly. S17W4-C.
 */

export type EquipmentUnitStatus =
  | 'AVAILABLE'
  | 'RESERVED'
  | 'IN_USE'
  | 'DAMAGED'
  | 'UNDER_REPAIR'
  | 'SOLD'
  | 'WITHDRAWAL_REQUESTED'
  | 'RETIRED';

export const EQUIPMENT_UNIT_STATUSES: ReadonlyArray<EquipmentUnitStatus> = [
  'AVAILABLE',
  'RESERVED',
  'IN_USE',
  'DAMAGED',
  'UNDER_REPAIR',
  'SOLD',
  'WITHDRAWAL_REQUESTED',
  'RETIRED',
];

export interface EquipmentType {
  id: string;
  tenantId: string;
  name: string;
  category: string;
  sku: string;
  manufacturerSku: string | null;
  alternativeSkus: string[];
  brand: string | null;
  model: string | null;
  description: string | null;
  specsJson: Record<string, unknown> | null;
  /** STORY-1306 D5 Phase 1: catalog/webshop-face hero image URL. */
  heroImageUrl: string | null;
  /** STORY-1306 D5 Phase 1: gallery image URL list. Phase 2 EquipmentTypeImage child tábla migrates. */
  galleryImages: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface EquipmentUnit {
  id: string;
  tenantId: string;
  typeId: string;
  serialNumber: string;
  assetTag: string | null;
  status: EquipmentUnitStatus;
  operatingHours: number | null;
  locationId: string | null;
  purchaseDate: Date | null;
  purchasePrice: number | null;
  warrantyEndAt: Date | null;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ServicePackage {
  id: string;
  tenantId: string;
  typeId: string;
  name: string;
  description: string | null;
  intervalHours: number | null;
  intervalDays: number | null;
  price: number | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/** Allowed transitions for the EquipmentUnit state machine (Sally 04-cikk §3.4). */
export const EQUIPMENT_UNIT_TRANSITIONS: Readonly<
  Record<EquipmentUnitStatus, ReadonlyArray<EquipmentUnitStatus>>
> = {
  AVAILABLE: ['RESERVED', 'IN_USE', 'DAMAGED', 'UNDER_REPAIR', 'SOLD', 'WITHDRAWAL_REQUESTED'],
  RESERVED: ['AVAILABLE', 'IN_USE', 'DAMAGED'],
  IN_USE: ['AVAILABLE', 'DAMAGED', 'UNDER_REPAIR'],
  DAMAGED: ['UNDER_REPAIR', 'RETIRED', 'WITHDRAWAL_REQUESTED'],
  UNDER_REPAIR: ['AVAILABLE', 'RETIRED', 'WITHDRAWAL_REQUESTED'],
  SOLD: [],
  WITHDRAWAL_REQUESTED: ['RETIRED', 'AVAILABLE'],
  RETIRED: [],
};

export function canTransition(from: EquipmentUnitStatus, to: EquipmentUnitStatus): boolean {
  return EQUIPMENT_UNIT_TRANSITIONS[from].includes(to);
}
